import {
  Modal,
  UnstyledButton,
  TextInput,
  Button,
  Group,
  Alert,
} from '@mantine/core';
import { HiPencil, HiPlus } from 'react-icons/hi2';
import { useState } from 'react';
import useAbsences from '@/hooks/employee/useAbsences';
import useApproveAbsence from '@/hooks/employee/useApproveAbsence';
import useDeleteAbsence from '@/hooks/employee/useDeleteAbsence';
import useCreateAbsence from '@/hooks/employee/useCreateAbsence';
import {
  createAbsenceSchema,
  type CreateAbsence,
} from '@/schemas/API/POST/employee/absence/request';
import { useForm, zodResolver } from '@mantine/form';
import SectionErrorNotification from '@/components/Section/SectionErrorNotification';
import SectionLoadingOverlay from '@/components/Section/SectionLoadingOverlay';
import { type Absence } from '@/schemas/API/GET/employee/absence/response';
import dayjs from 'dayjs';
import { IconInfoCircle } from '@tabler/icons-react';
import { displayError } from '@/utils/error/errorHandler';
import useEmployeeSchedule from '@/hooks/employee/useEmployeeSchedule';
import { useAvailabilityRefresh } from '../../EmployeeAvailabilitySection';

export const TimeOffToBeApprovedCard: React.FC = () => {
  const { absences, error } = useAbsences();
  const { approveAbsence } = useApproveAbsence();
  const { deleteAbsence } = useDeleteAbsence();
  const { createAbsence } = useCreateAbsence();
  const createForm = useForm<CreateAbsence>({
    initialValues: {
      name: 'name',
      note: 'note',
      start: '',
      end: '',
    },
    validate: zodResolver(createAbsenceSchema),
  });
  const [isCreateError, setIsCreateError] = useState<boolean>(false);
  const [selectedAbsence, setSelectedAbsence] = useState<Absence | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const { mutate } = useEmployeeSchedule();
  const { refresh } = useAvailabilityRefresh();

  if (error) return <SectionErrorNotification />;
  if (!absences) return <SectionLoadingOverlay />;
  let timeOffDays = 0;
  let timeOffToBeApprovedDays = 0;
  const totalDays = 50;


  const daysBetween = (startD: string, endD: string) => {
    let startDate, endDate;
    if (startD.includes('.')) {
      const [day, month, year] = startD
        .replace(/\s+/g, '')
        .split('.')
        .map(Number);
      startDate = new Date(year, month - 1, day);
    } else {
      startDate = new Date(startD);
    }
    if (endD.includes('.')) {
      const [day, month, year] = endD.replace(/\s+/g, '').split('.').map(Number);
      endDate = new Date(year, month - 1, day);
    } else {
      endDate = new Date(endD.replace(/\s+/g, ''));
    }
    const differenceInMs = endDate.getTime() - startDate.getTime();
    const differenceInDays = differenceInMs / (1000 * 60 * 60 * 24);
    return Math.ceil(differenceInDays);
  };
  
  
  absences.content.forEach(absence => {
    if (absence.confirm) {
      timeOffDays += daysBetween(absence.start, absence.end);
    }
    if (!absence.confirm) {
      timeOffToBeApprovedDays += 1;
    }
  });
  const formatDateForAPI = (date: string) => {
    if (!date) return '';
    date = date.replace(/\s+/g, '');
    if (date.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)) {
      const [day, month, year] = date.split('.');
      const paddedDay = day.padStart(2, '0');
      const paddedMonth = month.padStart(2, '0');
      return `${year}-${paddedMonth}-${paddedDay}`;
    }
    return date;
  };
  const closeModal = () => {
    setSelectedAbsence(null);
  };
  const openModal = (absenceData: Absence) => {
    setSelectedAbsence(absenceData);
  };
  const openCreateModal = () => {
    setIsCreateOpen(true);
  };
  const closeCreateModal = () => {
    setIsCreateOpen(false);
    createForm.reset();
    setIsCreateError(false);
  };
  const dateToShow = (date: string) => {
    const dateWithoutTime = date.split('T')[0];
    return dayjs(dateWithoutTime).format('DD.MM.YYYY');
  };
  async function handleDelete(id: number) {
    await deleteAbsence(id);
    closeModal();
    await mutate();
    refresh();
  }
  async function handleApprove(id: number) {
    await approveAbsence(id);
    closeModal();
    await mutate();
  }
  async function handleCreate() {
    const valRes = createForm.validate();
    if (valRes.hasErrors) {
      console.error('Invalid create form values:', valRes.errors);
      return;
    }
    if (
      timeOffDays +
        daysBetween(createForm.values.start, createForm.values.end) >
      totalDays
    ) {
      console.error('Length of absence exceeds 50 days.');
      setIsCreateError(true);
      return;
    }

    try {
      const apiData = {
        ...createForm.values,
        start: formatDateForAPI(createForm.values.start),
        end: formatDateForAPI(createForm.values.end),
      };
      await createAbsence(apiData);
    } catch (err) {
      displayError(err);
      setIsCreateError(true);
      return;
    }

    createForm.reset();
    setIsCreateError(false);
    closeCreateModal();
    await mutate();
    refresh();
  }
  return (
    <>
      {isCreateOpen && (
        <Modal
          size="xl"
          opened={true}
          onClose={closeCreateModal}
          centered
          title="Dovolená"
        >
          <div className="flex flex-row gap-3 mb-5">
            <div className="w-1/2 flex flex-col">
              <TextInput
                variant="filled"
                radius="md"
                placeholder="1.1.2024"
                label="Začátek"
                {...createForm.getInputProps('start')}
              />
            </div>
            <div className="w-1/2 flex flex-col justify-between">
              <TextInput
                variant="filled"
                radius="md"
                placeholder="31.1.2024"
                label="Konec"
                {...createForm.getInputProps('end')}
              />
            </div>
          </div>
          <p className="text-sm mb-4 mt-3">
            Délka dovolené:{' '}
            {createForm.values.start === '' || createForm.values.end === ''
              ? '-'
              : daysBetween(
                  createForm.values.start,
                  createForm.values.end
                )}
            dnů
          </p>
          <TextInput
            variant="filled"
            radius="md"
            placeholder="note"
            label="Poznámka"
            {...createForm.getInputProps('note')}
          />
          <h2 className="text-lg font-semibold">Počet dní dovolené:</h2>
          <p className="text-sm mb-4 mt-3">
            Schválené dovolené: {timeOffDays} dnů
          </p>
          <p className="text-sm mb-4 mt-3">
            Schválené dovolené + nová:{' '}
            {timeOffDays +
              daysBetween(createForm.values.start, createForm.values.end)}{' '}
            dnů (/{totalDays} maximum)
          </p>
          <Group justify="space-between" grow align="start" className="mt-4">
            <Button
              variant="outline"
              radius="md"
              onClick={closeCreateModal}
              className="w-full"
            >
              Odstranit
            </Button>
            <Button
              variant="filled"
              color="indigo"
              radius="md"
              onClick={handleCreate}
              className="w-full"
            >
              Vytvořit
            </Button>
          </Group>
          {isCreateError && (
            <Alert
              variant="light"
              color="red"
              radius="xl"
              title="Chyba!"
              icon={<IconInfoCircle />}
              className="mb-3"
            >
              Nekorektní údaje, zkontrolujte kolize nebo délku dovolené.
            </Alert>
          )}
        </Modal>
      )}
      {selectedAbsence && (
        <Modal
          size="xl"
          opened={true}
          onClose={closeModal}
          centered
          title="Dovolená"
        >
          <div className="flex flex-row gap-3 mb-5">
            <div className="w-1/2 flex flex-col">
              <TextInput
                variant="filled"
                radius="md"
                placeholder="note"
                label="Začátek"
                value={dateToShow(selectedAbsence.start)}
                readOnly
              />
            </div>
            <div className="w-1/2 flex flex-col justify-between">
              <TextInput
                variant="filled"
                radius="md"
                placeholder="note"
                label="Konec"
                value={dateToShow(selectedAbsence.end)}
                readOnly
              />
            </div>
          </div>
          <p className="text-sm mb-4 mt-3">
            Délka dovolené:{' '}
            {daysBetween(selectedAbsence.start, selectedAbsence.end)} dnů
          </p>
          <TextInput
            variant="filled"
            radius="md"
            placeholder="note"
            label="Poznámka"
            value={selectedAbsence.note}
            readOnly
          />
          <h2 className="text-lg font-semibold">Počet dní dovolené:</h2>
          <p className="text-sm mb-4 mt-3">
            Schválené dovolené: {timeOffDays} dnů
          </p>
          <p className="text-sm mb-4 mt-3">
            Schválené dovolené + nová:{' '}
            {timeOffDays +
              daysBetween(selectedAbsence.start, selectedAbsence.end)}{' '}
            dnů (/{totalDays} maximum)
          </p>
          <Group justify="space-between" grow align="start" className="mt-4">
            <Button
              variant="outline"
              radius="md"
              onClick={() => {
                handleDelete(selectedAbsence.id);
              }}
              className="w-full"
            >
              Odstranit
            </Button>
            <Button
              variant="filled"
              color="indigo"
              radius="md"
              onClick={() => {
                handleApprove(selectedAbsence.id);
              }}
              className="w-full"
            >
              Schválit
            </Button>
          </Group>
        </Modal>
      )}
      <div className="w-full box-border border border-card rounded-card p-2.5">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold">Dovolené ke schválení</h2>
          <UnstyledButton onClick={openCreateModal}>
            <div className="flex items-center">
              <HiPlus color="black" size="1.5em" />
            </div>
          </UnstyledButton>
        </div>
        {timeOffToBeApprovedDays > 0 ? (
          <>
            <ul>
              {absences.content.map(a =>
                !a.confirm ? (
                  <li
                    key={a.id}
                    className="flex justify-between items-center mb-2"
                  >
                    <span>
                      {dateToShow(a.start)} - {dateToShow(a.end)}
                    </span>
                    <UnstyledButton onClick={() => openModal(a)}>
                      <div className="flex items-center">
                        <HiPencil color="black" size="1.5em" />
                      </div>
                    </UnstyledButton>
                  </li>
                ) : null
              )}
            </ul>
          </>
        ) : (
          <h3 className="text-sm mb-4 mt-3">
            Nejsou žádné dovolené ke schválení
          </h3>
        )}
      </div>
    </>
  );
};
